import React, {
  useEffect,
  createContext,
  useContext,
  useMemo,
  useState,
  useRef,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { usePersistState } from 'helper';
import MaskNames from 'modules/reports/containers/MaskNames';
import {
  useBalanceList,
  useClientList,
  useExportBalance,
  useExportClient,
  useExportRegistration,
  useExportSenderName,
  useExportSmsUsage,
  useExportTransactions,
  useExportWalletBalance,
  useRegistrationList,
  useSenderNameList,
  useSmsUsageList,
  useTransactionList,
  useWalletBalanceList,
} from 'modules/reports/hooks';
import { useOrganizationList } from 'modules/subscribers/hooks';
import SmsUsage from 'modules/reports/containers/SmsUsage';
import PaymentTransactions from 'modules/reports/containers/PaymentTransactions';
import SubscriberBalance from 'modules/reports/containers/SubscriberBalance';
import { omit, pickBy } from 'lodash';
import ClientInformation from 'modules/reports/containers/ClientInformation';
import RegistrationList from 'modules/reports/containers/RegistrationReport';
import WalletBalance from 'modules/reports/containers/WalletBalance';

const ReportsContext = createContext();

const config = {
  'client-information': {
    title: 'Clients Information',
    exportFileName: 'Clients_Information',
    component: ClientInformation,
    hook: useClientList,
    exportHook: useExportClient,
  },
  'mask-name': {
    title: 'Mask Name',
    exportFileName: 'Mask_Names',
    component: MaskNames,
    hook: useSenderNameList,
    exportHook: useExportSenderName,
  },
  'sms-usage': {
    title: 'SMS Usage Count and SMS Total Amount Consumed',
    exportFileName: 'SMS_Usage_Consumption',
    component: SmsUsage,
    hook: useSmsUsageList,
    exportHook: useExportSmsUsage,
    withFilter: true,
  },
  'payment-transactions': {
    title: `Payment Transactions (Top-up credit load and mask name's subscription)`,
    exportFileName: 'Payment_Transactions',
    component: PaymentTransactions,
    hook: useTransactionList,
    exportHook: useExportTransactions,
    withFilter: true,
  },
  'current-balance': {
    title: `Subscriber's Current Balance (Credit Load-Wallet)`,
    exportFileName: 'Subscriber_Balance',
    component: SubscriberBalance,
    hook: useBalanceList,
    exportHook: useExportBalance,
  },
  'registration-report': {
    title: 'Registration report (Total registered and total approved)',
    exportFileName: 'Registration_Report',
    component: RegistrationList,
    hook: useRegistrationList,
    exportHook: useExportRegistration,
    withFilter: true,
  },
  'wallet-balance': {
    title: 'Wallet Balance',
    exportFileName: 'Wallet_Balance',
    component: WalletBalance,
    hook: useWalletBalanceList,
    exportHook: useExportWalletBalance,
    withFilter: true,
    withOrgFilter: true,
    withTypeFilter: true,
    withStatusFilter: true,
  },
};

function ReportsProvider({ children }) {
  const [filter, setFilter] = useState({
    from: '',
    to: '',
  });
  const [page, setPage] = useState({ page: '' });
  const [organizationId, setOrganizationId] = useState('');
  const [walletType, setWalletType] = useState('');
  const [walletStatus, setWalletStatus] = useState('');
  const [orgSearch, setOrgSearch] = useState('');
  const [debouncedOrgSearch, setDebouncedOrgSearch] = useState('');
  const debounceRef = useRef(null);
  const handleOrgSearch = useCallback((q) => {
    setOrgSearch(q);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedOrgSearch(q), 300);
  }, []);
  const [, organizationList] = useOrganizationList({
    q: debouncedOrgSearch || undefined,
    per_page: 20,
  });
  const organizationOptions = useMemo(
    () =>
      (organizationList || []).map((org) => ({
        label: org?.name,
        value: org?.uuid,
      })),
    [organizationList],
  );
  const payload = useMemo(
    () =>
      pickBy({
        date_from: filter?.from,
        date_to: filter?.to,
        organization_uuid: organizationId,
        type: walletType,
        status: walletStatus,
        ...page,
      }),
    [filter, organizationId, walletType, walletStatus, page],
  );

  const [current, setCurrent] = usePersistState(
    'reports-selected',
    {
      type: { label: 'Clients Information', value: 'client-information' },
    },
    {
      keepOnUnmount: false,
    },
  );

  const currentConfig = useMemo(
    () => config?.[current?.type?.value],
    [current?.type?.value],
  );

  const { hook, exportHook } = currentConfig;
  const [isLoading, list, meta] = hook(payload);
  const [isExporting, exportData] = exportHook(omit(payload, ['page']));

  useEffect(() => {
    setPage({ page: '' });
  }, [filter, current]);

  useEffect(() => {
    setFilter({ from: '', to: '' });
    setOrganizationId('');
    setWalletType('');
    setWalletStatus('');
    setOrgSearch('');
    setDebouncedOrgSearch('');
  }, [current]);

  const valueObject = useMemo(
    () => ({
      current,
      setCurrent,
      currentConfig,
      isLoading,
      list,
      meta,
      setPage,
      setFilter,
      isExporting,
      exportData,
      payload,
      organizationId,
      setOrganizationId,
      organizationOptions,
      orgSearch,
      handleOrgSearch,
      walletType,
      setWalletType,
      walletStatus,
      setWalletStatus,
    }),
    [
      payload,
      current,
      setCurrent,
      currentConfig,
      isLoading,
      list,
      meta,
      setFilter,
      isExporting,
      exportData,
      organizationId,
      setOrganizationId,
      organizationOptions,
      orgSearch,
      handleOrgSearch,
      walletType,
      setWalletType,
      walletStatus,
      setWalletStatus,
    ],
  );

  return (
    <ReportsContext.Provider value={valueObject}>
      {children}
    </ReportsContext.Provider>
  );
}

const useReportsContext = () => useContext(ReportsContext);

ReportsProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
    PropTypes.string,
  ]).isRequired,
};

export { ReportsProvider, useReportsContext };
